import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCogs, faLightbulb, faRocket, faChevronDown, faQuoteLeft } from '@fortawesome/free-solid-svg-icons';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ServiceCard from '../components/ServiceCard';
import ProjectCard from '../components/ProjectCard';
import BookingButton from '../components/BookingButton';
import { trustData, services, projects, testimonials, faqs } from '../data/content';

const processSteps = [
  { title: 'Your Idea', description: 'Share your vision and goals with us.', icon: faLightbulb },
  { title: 'Development by Golden Coast', description: 'We transform your idea into reality.', icon: faCogs },
  { title: 'Your Successful Business', description: 'Launch and grow with confidence.', icon: faRocket },
];

const SectionHeading = ({ title, subtitle }) => (
  <div className="text-center mb-12">
    <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">{title}</h2>
    {subtitle && <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">{subtitle}</p>}
  </div>
);

const Home = () => {
  return (
    <div className="bg-white text-gray-900">
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-b from-blue-100 via-blue-50 to-white pt-40 pb-8 sm:pt-48 sm:pb-12">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <span className="inline-block bg-white text-blue-700 text-sm font-semibold px-4 py-1 rounded-full shadow-sm mb-6">
            Built locally in Southern California
          </span>
          <h1 className="text-5xl sm:text-6xl font-extrabold tracking-tight mb-6">
            Your Ideas, <span className="text-blue-600">Our Expertise</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-700 mb-10 max-w-3xl mx-auto">
            Turn your million dollar idea into reality. Berkeley-trained engineers design, build, and launch
            your custom app or website, with daily progress updates from kickoff to launch.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <BookingButton />
            <a
              href="/#services"
              className="bg-white border-2 border-blue-600 text-blue-700 px-6 py-3 rounded-md font-bold hover:bg-blue-600 hover:text-white transition-transform transform hover:scale-105 text-center"
            >
              Explore Our Services
            </a>
          </div>

          <div className="mt-16">
            <p className="text-sm font-semibold uppercase tracking-wider text-gray-500 mb-5">Recently shipped</p>
            <div className="flex flex-wrap justify-center items-center gap-8 sm:gap-12">
              {projects.map((project) => (
                <a
                  key={project.title}
                  href={project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-gray-700 hover:text-blue-600 transition"
                >
                  <img src={project.logo} alt="" className="h-10 w-10 object-contain rounded-lg" />
                  <span className="font-semibold">{project.title}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col items-center space-y-10 sm:flex-row sm:space-y-0 sm:space-x-12 text-center">
            {processSteps.map((step, index) => (
              <React.Fragment key={step.title}>
                {index > 0 && <div className="flex-grow h-1 bg-blue-100 hidden sm:block" />}
                <div className="flex flex-col items-center">
                  <div className="flex items-center justify-center w-16 h-16 bg-blue-600 text-white rounded-full shadow-md">
                    <FontAwesomeIcon icon={step.icon} size="2x" />
                  </div>
                  <h3 className="text-xl font-bold mt-4">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section id="why-choose-us" className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeading
            title="Why Clients Trust Us"
            subtitle="With over a decade of engineering experience and a team of top Berkeley graduates, Golden Coast Software Development delivers software with transparency, efficiency, and professionalism."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {trustData.map((item) => (
              <ServiceCard key={item.title} {...item} />
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeading
            title="Our Services"
            subtitle="Everything you need to go from idea to launch. Tap a card to learn more."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <ServiceCard key={service.title} {...service} />
            ))}
          </div>
        </div>
      </section>

      {/* Projects */}
      <section id="recently-completed-projects" className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeading title="Recently Completed Projects" subtitle="Real products, live today." />
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-8">
            {projects.map((project) => (
              <ProjectCard key={project.title} {...project} />
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section id="testimonials" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <SectionHeading title="What Our Clients Say" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial) => (
                <figure key={testimonial.name} className="bg-blue-50 rounded-lg p-8">
                  <FontAwesomeIcon icon={faQuoteLeft} className="text-blue-300 text-3xl mb-4" />
                  <blockquote className="text-gray-700 leading-relaxed">{testimonial.quote}</blockquote>
                  <figcaption className="mt-6">
                    <p className="font-bold">{testimonial.name}</p>
                    {testimonial.role && <p className="text-sm text-gray-500">{testimonial.role}</p>}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section id="faq" className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <SectionHeading title="Frequently Asked Questions" />
          <div className="divide-y divide-gray-200 border-y border-gray-200">
            {faqs.map((faq) => (
              <details key={faq.question} className="group py-5">
                <summary className="flex justify-between items-center cursor-pointer list-none text-lg font-semibold text-gray-900 hover:text-blue-600 transition">
                  {faq.question}
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className="ml-4 text-blue-600 transition-transform group-open:rotate-180"
                  />
                </summary>
                <p className="mt-3 text-gray-600 leading-relaxed">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-lg text-blue-100 mb-8">
            Book a free consultation and we'll turn your idea into a clear plan, timeline, and proposal.
          </p>
          <BookingButton variant="light" />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
